import { lazy, Suspense, useContext } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import RegisterUserPage from "./pages/register/RegisterUserPage";
import MicPermissionHelp from "./pages/help/MicPermission";
import EndCallModal from "./components/EndCallModal";
import MissedCallModal from "./components/MissedCallModal";
import CallErrorSnackbar from "./components/CallErrorSnackbar";
import AddCaregiverPage from "./pages/addCaregiver/AddCaregiverPage";
import AddNewAssignmentPage from "./pages/assignmentCenter/AddNewAssignmentPage";
import AddClientPage from "./pages/addNewClient/AddClientPage";
import AssignmentCenterPage from "./pages/assignmentCenter/AssignmentCenterPage";
import AssignmentSetSucessPage from "./pages/assignmentCenter/AssignmentSetSucessPage";
import ClientProfilePage from "./pages/clientProfile/ClientProfilePage";
import ContactsPage from "./pages/contacts/ContactsPage";
import { ViewContactPage } from "./pages/contacts/ViewContactPage";
import InviteSentPage from "./pages/invite/InviteSentPage";
import ChangePasswordPage from "./pages/login/ChangePasswordPage";
import ClientAddSuccessPage from "./pages/login/ClientAddSuccessPage";
import ConfirmAccountPage from "./pages/login/ConfirmAccountPage";
import ForgotPasswordPage from "./pages/login/ForgotPasswordPage";
import LoginPage from "./pages/login/LoginPage";
import ResetPasswordPage from "./pages/login/ResetPasswordPage";
import SelectClientHomepage from "./pages/home/SelectClientHomepage";
import WebOverviewRoute from "./pages/web/WebOverviewRoute";
import WebVitalsRoute from "./pages/web/WebVitalsRoute";
import WebClientsRoute from "./pages/web/WebClientsRoute";
import WebAlertsRoute from "./pages/web/WebAlertsRoute";
import WebMapRoute from "./pages/web/WebMapRoute";
import WebIntelRoute from "./pages/web/WebIntelRoute";
import WebSitrepRoute from "./pages/web/WebSitrepRoute";
import WebDispatchRoute from "./pages/web/WebDispatchRoute";
import WebScheduleRoute from "./pages/web/WebScheduleRoute";
import WebMessagesRoute from "./pages/web/WebMessagesRoute";
import WebReportsRoute from "./pages/web/WebReportsRoute";
import WebAuditSettingsRoute from "./pages/web/WebAuditSettingsRoute";
import WebDevicesRoute from "./pages/web/WebDevicesRoute";
import WebSectionPlaceholder from "./pages/web/WebSectionPlaceholder";
import TermsPage from "./pages/terms/TermsPage";
import { DEFAULT_HOME } from "./utils/defaultHome";
import { COMMAND_CENTER_ENABLED } from "./utils/commandCenterFlag";
import {
  notePriorSession,
  markSessionEndIfPriorSession,
} from "./config/sessionTimeout";
import { AuthContext } from "./state/AuthContext";
import { CallV3Provider } from "./state/CallV3Context";
import { ClientProvider } from "./state/ClientContext";
import { ContactProvider } from "./state/ContactContext";
import { ActivityProvider } from "./state/ActivityContext";
import CaregiverProfilePage from "./pages/caregiverProfile/CaregiverProfilePage";
import { ViewActivityPage } from "./pages/activity/viewActivity/ViewActivityPage";
import "./pages/activity/viewActivity/ViewActivityPage.css";
import AddActivityPage from "./pages/activity/addActivity/AddActivityPage";
import "./pages/activity/addActivity/AddActivityPage.css";
import ClientCalendar from "./pages/activity/clientCalendar/ClientCalendar";
import "./pages/activity/clientCalendar/ClientCalendar.css";
import "./pages/alerts/AlertsPage.css";

// GMA-1180: Call stack (VideoCallV3 -> ClientAgora/OverviewAgora -> agora-rtc-react) must
// not be in the main chunk for /login, /register, /accept-terms, etc. Lazy-load on demand.
const CallPage = lazy(() => import("./pages/calling/CallPage"));
const CallReceiver = lazy(() => import("./pages/calling/CallReceiver"));
const VideoCallV3Page = lazy(() => import("./pages/calling/VideoCallV3Page"));

const callingFullPageFallback = (
  <div
    style={{
      display: "flex",
      minHeight: "100vh",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    Loading call…
  </div>
);

const callingInlineFallback = (
  <div className="p-3" style={{ minHeight: "2rem" }}>
    Loading call…
  </div>
);

const RequireLogin = ({ children }) => {
  const { isLoggedIn, isLoading } = useContext(AuthContext);
  // Issue #672 — the third silent path to /login. `!isLoggedIn` here covers two
  // very different users: one whose token vanished mid-session, and one who
  // typed a protected URL while signed out. Only the first is an unexplained
  // sign-out; a notice for the second would be a lie.
  //
  // Both calls are made during render, deliberately: the notice has to be in
  // sessionStorage BEFORE LoginPage's mount effect reads it, and an effect on
  // this component is not ordered against that. Both are idempotent, which is
  // what makes a render-time call safe under StrictMode's double invocation.
  if (isLoggedIn) notePriorSession();
  if (isLoading) return null;
  if (isLoggedIn) return children;
  // A no-op unless a session really existed, and it never overwrites a reason
  // already marked — the idle path marks "idle" and then arrives here.
  markSessionEndIfPriorSession();
  return <Navigate replace to="/login" />;
};

const RedirectIfAuthenticated = ({ children }) => {
  const { isAuthenticated, isLoggedIn, isLoading } = useContext(AuthContext);
  if (isLoading) return null;
  if (!isAuthenticated) return children;
  if (!isLoggedIn) return <Navigate replace to="/accept-terms" />;
  return <Navigate replace to={DEFAULT_HOME} />;
};

const RequireAdmin = ({ children }) => {
  const { currentUser, isLoggedIn, isLoading } = useContext(AuthContext);
  if (isLoading) return null;
  return isLoggedIn && (currentUser?.isAdmin || currentUser?.isSuperAdmin) ? (
    children
  ) : (
    <Navigate replace to={DEFAULT_HOME} />
  );
};

const RequireSuperAdmin = ({ children }) => {
  const { currentUser, isLoggedIn, isLoading } = useContext(AuthContext);
  if (isLoading) return null;
  return isLoggedIn && currentUser?.isSuperAdmin ? (
    children
  ) : (
    <Navigate replace to={DEFAULT_HOME} />
  );
};

const RootRedirect = () => {
  const { isAuthenticated, isLoggedIn, isLoading } = useContext(AuthContext);
  if (isAuthenticated) notePriorSession();
  if (isLoading) return null;
  if (!isAuthenticated) {
    // Same rule as RequireLogin: a no-op unless this page load held a session,
    // and it never overwrites a reason already marked (PR #698 review — this
    // guard and NotFoundRedirect reach /login too, and were silent).
    markSessionEndIfPriorSession();
    return <Navigate to="/login" replace />;
  }
  if (!isLoggedIn) return <Navigate to="/accept-terms" replace />;
  return <Navigate to={DEFAULT_HOME} replace />;
};

const NotFoundRedirect = () => {
  const { isLoggedIn, isLoading } = useContext(AuthContext);
  if (isLoggedIn) notePriorSession();
  if (isLoading) return null;
  if (!isLoggedIn) {
    markSessionEndIfPriorSession();
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={DEFAULT_HOME} replace />;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/*
        GMA-1241: /terms is now a public, static, read-only User Agreement at
        public/terms/index.html, served by Apache before this SPA is reached.
        The in-app User Agreement *acceptance* flow lives at /accept-terms.
        We also keep /terms as a SPA route that immediately redirects to
        /accept-terms so any deep-link (e.g. from an old email) still works
        when the static asset is missing in some build configurations.
      */}
      <Route path="/terms" element={<Navigate to="/accept-terms" replace />} />
      <Route path="/accept-terms" element={<TermsPage />} />
      <Route path="/register" element={<RegisterUserPage />} />
      {/*
        GMA-1363: Public, unauthenticated help page for caregivers who
        scan the QR code in the MyCompass iOS app's mic-permission
        blocked banner. Must stay reachable without a session — the
        caregiver may not be logged into compasscare.app on the phone
        they're scanning with.
      */}
      <Route path="/help/mic-permission" element={<MicPermissionHelp />} />
      <Route
        path="/login"
        element={
          <RedirectIfAuthenticated>
            <LoginPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/users/change-password"
        element={
          <RedirectIfAuthenticated>
            <ChangePasswordPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/users/forgot-password"
        element={
          <RedirectIfAuthenticated>
            <ForgotPasswordPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/users/reset-password"
        element={
          <RedirectIfAuthenticated>
            <ResetPasswordPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/users/confirm_account"
        element={
          <RedirectIfAuthenticated>
            <ConfirmAccountPage />
          </RedirectIfAuthenticated>
        }
      />
      <Route
        path="/call"
        element={
          <Suspense fallback={callingFullPageFallback}>
            <VideoCallV3Page />
          </Suspense>
        }
      />
      <Route
        element={
          <RequireLogin>
            <ClientProvider>
              <CallV3Provider>
                <Suspense fallback={null}>
                  <CallReceiver />
                </Suspense>
                <EndCallModal />
                <MissedCallModal />
                <CallErrorSnackbar />
                <Outlet />
              </CallV3Provider>
            </ClientProvider>
          </RequireLogin>
        }
      >
        <Route
          path="/select-client-homepage"
          element={<SelectClientHomepage />}
        />
        {/* GMA-1617: CompassCare Web facelift — Overview command-center. Other
            /web/<section> routes render a placeholder inside the shell until
            their story lands, so sidebar navigation never dead-ends. */}
        {/* react-app#427: the whole /web/* block is gated behind the
            build-time REACT_APP_COMMAND_CENTER_ENABLED flag so production
            builds exclude the command-center UI. When disabled, /web/* URLs
            fall through to the existing unknown-route handling (the "*"
            NotFoundRedirect below). */}
        {COMMAND_CENTER_ENABLED && (
          <>
            <Route path="/web/overview" element={<WebOverviewRoute />} />
            <Route path="/web/vitals" element={<WebVitalsRoute />} />
            <Route path="/web/clients" element={<WebClientsRoute />} />
            <Route path="/web/alerts" element={<WebAlertsRoute />} />
            <Route path="/web/map" element={<WebMapRoute />} />
            <Route path="/web/intel" element={<WebIntelRoute />} />
            {/* GMA-1624: SITRep board nests under INTEL so findNavSectionByPath
                keeps the sidebar honest without touching the frozen 13-section
                nav. */}
            <Route path="/web/intel/sitrep" element={<WebSitrepRoute />} />
            <Route path="/web/dispatch" element={<WebDispatchRoute />} />
            <Route path="/web/schedule" element={<WebScheduleRoute />} />
            <Route path="/web/messages" element={<WebMessagesRoute />} />
            <Route path="/web/reports" element={<WebReportsRoute />} />
            {/* GMA-1629: one component serves BOTH sidebar entries — Audit Log
                and Settings — with the matching tab preselected per path. */}
            <Route
              path="/web/audit"
              element={<WebAuditSettingsRoute section="audit" />}
            />
            <Route
              path="/web/settings"
              element={<WebAuditSettingsRoute section="settings" />}
            />
            <Route path="/web/devices" element={<WebDevicesRoute />} />
            <Route path="/web/:section/*" element={<WebSectionPlaceholder />} />
          </>
        )}
        <Route
          path="/calendar/:clientId"
          element={
            <ActivityProvider>
              <ClientCalendar />
            </ActivityProvider>
          }
        />
        <Route
          path="/contacts/:clientId"
          element={
            <ContactProvider>
              <ContactsPage />
            </ContactProvider>
          }
        />
        <Route path="/view_contact/:contactId" element={<ViewContactPage />} />
        <Route
          path="/caregiver_profile/:caregiverId"
          element={<CaregiverProfilePage />}
        />
        <Route
          path="/client_profile/:clientId"
          element={<ClientProfilePage />}
        />
        <Route
          path="/add-activity"
          element={
            <ActivityProvider>
              <AddActivityPage />
            </ActivityProvider>
          }
        />
        <Route
          path="/view-activity/:activityId"
          element={
            <ActivityProvider>
              <ViewActivityPage />
            </ActivityProvider>
          }
        />
        <Route
          path="/call/:clientId"
          element={
            <Suspense fallback={callingInlineFallback}>
              <CallPage />
            </Suspense>
          }
        />
        <Route path="/invite-sent" element={<InviteSentPage />} />
        <Route
          path="/assignment-set-success"
          element={<AssignmentSetSucessPage />}
        />
        <Route path="/client-add-success" element={<ClientAddSuccessPage />} />
        <Route
          path="/assignment_center"
          element={
            <RequireAdmin>
              <AssignmentCenterPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/add_new_assignment"
          element={
            <RequireAdmin>
              <AddNewAssignmentPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/add-client"
          element={
            <RequireAdmin>
              <AddClientPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/add-caregiver"
          element={
            <RequireAdmin>
              <AddCaregiverPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/add-admin"
          element={
            <RequireSuperAdmin>
              <AddCaregiverPage />
            </RequireSuperAdmin>
          }
        />
      </Route>
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<NotFoundRedirect />} />
    </Routes>
  );
};

export default AppRoutes;
